import { useTaskReviews } from '../../hooks/useTaskReviews';
import { useAdminCheckCounts } from '../../hooks/useAdminChecks';
import type { NavLeaf } from './navConfig';

/**
 * Live counter beside a nav item. Reads the shared queries (kept fresh by
 * socket invalidation in useRealtime) and renders nothing when the count
 * is zero, so the chip only ever signals real work. In the collapsed rail
 * there's no room for a number, so show a small dot instead.
 */
export function NavBadge({
  kind,
  collapsed = false,
}: {
  kind: NonNullable<NavLeaf['badge']>;
  collapsed?: boolean;
}) {
  const reviews = useTaskReviews(kind === 'task-reviews');
  // DBS applications waiting on HR review; later stages are either with
  // the inductee or gated on the day-one physical certificate check.
  const dbs = useAdminCheckCounts('dbs', kind === 'dbs-checks');
  const count =
    kind === 'task-reviews'
      ? reviews.data?.length ?? 0
      : dbs.data?.applied ?? 0;
  if (count <= 0) return null;

  if (collapsed) {
    return (
      <span
        aria-label={`${count} awaiting review`}
        className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-warning-soft ring-2 ring-surface"
      />
    );
  }

  return (
    <span
      aria-label={`${count} awaiting review`}
      className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-warning-soft/15 px-1.5 text-[11px] font-semibold tabular-nums text-warning-soft-fg"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
